/**
 * Anzeige-Regeln für die Probezeit (Spezifikation 2026-09-16, R2/R3/R7).
 *
 * Die Rechnung selbst steht im Backend (`src/services/probezeit-*.ts`) und ist
 * live. Hier stehen alle Regeln der Anzeige: Rundung, Zahlenform und Vorzeichen,
 * Beschriftungen und zusammengesetzte Sätze, Farben und Reihenfolge. Die Bausteine
 * (`components/probezeit/`) ordnen nur an. Die Datei bleibt bewusst ohne React, damit
 * die Regeln ohne Oberfläche testbar sind (dieselbe Trennung wie
 * `lib/tabellen-sortierung.ts`, siehe Kommentar dort).
 *
 * Wortlaute für Woche 0 und die Stand-Zeile: Marie 08.10.2026.
 */
// Die Werte, die das Backend schickt, stehen genau einmal: in `api.ts`.
// Hier nur als Typ-Import — zur Laufzeit lädt diese Datei dadurch nichts aus `api.ts`, der
// Wächtertest gegen React bleibt also gültig. Zur Laufzeit lädt sie nur `krankheitstage.ts`,
// eine Datei ohne eigene Importe.
import type {
  NichtGerechnetGrund,
  ProbezeitAmpel,
  ProbezeitErgebnis,
  ProbezeitReihenWoche,
  ProbezeitSatz,
  ProbezeitStufe,
  ProbezeitWirtschaft,
  ProbezeitZeile,
  Role,
} from "./api.js";
// Die Zählform für Arbeitstage gibt es schon (Mein Team, Mitarbeiterfenster): „1 Arbeitstag“, „2,5 Arbeitstage“.
import { arbeitstageText } from "./krankheitstage.js";
import type { SortSpalten } from "./tabellen-sortierung.js";

/** Urteil neben der Quote. Rot heißt begründungspflichtig, nicht Trennung (R3). */
export const AMPEL_URTEIL: Record<ProbezeitAmpel, string> = {
  keine: "noch keine Ampel",
  gruen: "auf Kurs",
  gelb: "beobachten",
  rot: "begründungspflichtig",
};

/** Füllfarbe der Ampel: für Flächen (Pille, Punkte, Balken), nie für Text. */
export const AMPEL_FARBE: Record<ProbezeitAmpel, string | null> = {
  keine: null,
  gruen: "var(--risk-low)",
  gelb: "var(--risk-medium)",
  rot: "var(--risk-high)",
};

/**
 * Textfarbe der Ampel. Text nimmt die kontrastgeprüften `-text`-Marken (>= 4,5 : 1, siehe
 * `ui/src/index.css`), nie die Füllfarbe: Gelb als Text erreicht nur etwa 2,4 bis 2,8 : 1.
 * Dieselbe Trennung wie in `EmployeeDetailDrawer.tsx` (`riskColor` und `riskTextColor`).
 */
export const AMPEL_TEXTFARBE: Record<ProbezeitAmpel, string | null> = {
  keine: null,
  gruen: "var(--risk-low-text)",
  gelb: "var(--risk-medium-text)",
  rot: "var(--risk-high-text)",
};

// Zahlen aus `config/wirtschaftlichkeit.yaml`, die hier in Texten und Regeln vorkommen.
// Das Backend rechnet mit denselben Werten; ändert sich dort einer, muss er hier mit.

/** Ab dieser Woche bewertet die Ampel (config/wirtschaftlichkeit.yaml: probezeit.ampelAbWoche). */
const AMPEL_AB_WOCHE = 4;

/**
 * Eine Woche zählt erst so viele Tage nach ihrem Ende, weil Stunden nachgebucht werden
 * (config/wirtschaftlichkeit.yaml: probezeit.karenzTage).
 */
const KARENZ_TAGE = 7;

/**
 * Länge der Probezeit in Wochen. Das Backend schickt sie je Person als `wochenGesamt`;
 * hier steht der Standard für `satzText` (config/wirtschaftlichkeit.yaml: probezeit.wochen).
 */
const PROBEZEIT_WOCHEN = 26;

/**
 * Wochenstunden einer Vollzeitstelle; wer weniger arbeitet, steht als Teilzeit da
 * (config/wirtschaftlichkeit.yaml: fullTimeWeeklyHours).
 */
const VOLLZEIT_STUNDEN = 40;

function wochenWort(n: number): string {
  return n === 1 ? "1 Woche" : `${n} Wochen`;
}

/**
 * Satz unter der Kostenzeile; welcher gilt, entscheidet das Backend (`satz`).
 * `wochenGesamt` ist die Länge der Probezeit: Die Oberfläche reicht den Wert des
 * Backends durch, damit „nach Woche N" der Konfiguration folgt. Ohne Angabe gilt
 * PROBEZEIT_WOCHEN.
 */
export function satzText(
  satz: ProbezeitSatz,
  restWochen: number,
  wochenGesamt: number = PROBEZEIT_WOCHEN,
): string {
  switch (satz) {
    case "keineWoche":
      return `Noch keine Woche gezählt. Eine Woche zählt erst ${KARENZ_TAGE} Tage nach ihrem Ende, weil Stunden nachgebucht werden. Die Ampel startet in Woche ${AMPEL_AB_WOCHE}.`;
    case "vollstaendigErreicht":
      return "Probezeit-Zeitraum vollständig: Break-even erreicht.";
    case "vollstaendigNichtErreicht":
      return "Probezeit-Zeitraum vollständig: Break-even nicht erreicht. Das heißt begründungspflichtig, nicht Trennung.";
    case "schonErreicht":
      return `Break-even schon erreicht. Die Probezeit läuft noch ${wochenWort(restWochen)}.`;
    case "nochKeineBewertung":
      return `Noch keine Bewertung, die Ampel startet in Woche ${AMPEL_AB_WOCHE}. Das Minus am Anfang ist eingeplant.`;
    case "laeuft":
      return `Noch ${wochenWort(restWochen)} Probezeit. Das Minus am Anfang ist eingeplant, nach Woche ${wochenGesamt} soll mindestens ±0 stehen.`;
  }
}

/** „05.01." */
export function ddMM(iso: string): string {
  const [, monat, tag] = iso.slice(0, 10).split("-");
  return `${tag}.${monat}.`;
}

/** Zeile unter den Wochenbalken; null, solange keine Woche gezählt ist. */
export function standZeile(leistungenBis: string | null): string | null {
  if (!leistungenBis) return null;
  return `Stand: Leistungen bis ${ddMM(leistungenBis)} — spätere Buchungen fehlen noch`;
}

/**
 * Beschriftung der senkrechten Linie im Wochenbalken: „Linie = bis hier gezählt“. Die Linie steht
 * hinter der letzten gezählten Woche, und eine Woche zählt erst 7 Tage nach ihrem Ende. Der
 * heutige Tag liegt also weiter rechts. „heute“ wäre genau das Missverständnis, das der
 * Woche-0-Satz (`satzText`) ausräumt. „bis hier gezählt“ statt „gezählt bis“, damit der Text nicht
 * auf einer Präposition endet.
 *
 * **Ohne Datum**, obwohl der Tag bekannt ist: Es ist derselbe Tag, den die Stand-Zeile direkt
 * unter dem Diagramm nennt. Zweimal dasselbe Datum unter zwei Beschriftungen, wenige Pixel
 * auseinander, ist genau der Widerspruch, über den eine Führungskraft stolpert, dieselbe
 * Entscheidung wie bei den Krankheits-Kacheln. Gezeichnet wird die Linie nur, wenn eine Woche
 * gezählt ist, deshalb braucht die Beschriftung kein Argument.
 */
export const LINIEN_LEGENDE = "Linie = bis hier gezählt";

/** Alle Texte des Wochenbalkens (R7). */
export interface WochenBalkenTexte {
  /** Name des Diagramms für Screenreader. */
  beschreibung: string;
  /** Titel, der beim Überfahren des Diagramms erscheint. */
  titel: string;
  /** Beschriftung am linken Ende der Achse. */
  anfang: string;
  /** Mitte unter dem Diagramm: was gestrichelt heißt, bei gezeichneter Linie auch die Linie. */
  legende: string;
  /** Beschriftung am rechten Ende der Achse. */
  ende: string;
}

/**
 * Texte des Wochenbalkens. `mitLinie` sagt, ob die senkrechte Linie gezeichnet wird (nicht bei
 * Woche 0 und nicht nach der letzten Woche): Nur dann steht sie in der Legende.
 */
export function wochenBalkenTexte(anzahlWochen: number, mitLinie: boolean): WochenBalkenTexte {
  const legende = "Stunden je Woche · gestrichelt = Ziel";
  return {
    beschreibung: `Stunden je Woche: Ziel und Ist für ${wochenWort(anzahlWochen)}`,
    titel: "Zielstunden gestrichelt, gebuchte Stunden gefüllt",
    anfang: "Woche 1",
    legende: mitLinie ? `${legende} · ${LINIEN_LEGENDE}` : legende,
    ende: `Woche ${anzahlWochen}`,
  };
}

/**
 * Tooltip einer Woche im Wochenbalken (R7): „Woche 6 (05.10.–11.10.): Ziel 20 h · Ist 18,5 h".
 * Die Stunden sind gerundet wie in der Tabelle. Bei Teilzeit ist das Wochenziel ein Produkt und
 * kommt ungerundet vom Backend (12 × 0,8 = 9,600000000000001), ungerundet stünde es im Tooltip.
 * Ohne gebuchte Stunden steht beim Ist ein Strich.
 */
export function wochenTooltip(woche: ProbezeitReihenWoche): string {
  const ist = woche.istStunden === null ? KEIN_WERT : stundenText(woche.istStunden);
  return `Woche ${woche.woche} (${ddMM(woche.von)}–${ddMM(woche.bis)}): Ziel ${stundenText(woche.zielStunden)} · Ist ${ist}`;
}

/**
 * Satz im Block einer Person, die erst noch anfängt: „Startet am 02.11." `ddMM` endet schon mit
 * einem Punkt, der den Satz schließt; ein zweiter ergäbe „02.11..".
 */
export function startetAmText(eintritt: string): string {
  return `Startet am ${ddMM(eintritt)}`;
}

/**
 * Zeile im Kasten „Wird nicht gerechnet": „Probezeit endet 28.12." Nur das Ende, denn dort gibt
 * es keine Woche und kein Ziel, und kein „Ziel und Kosten anteilig" über Werte, die nicht gerechnet werden.
 */
export function probezeitEndeText(probezeitEnde: string): string {
  return `Probezeit endet ${ddMM(probezeitEnde)}`;
}

/** Satz im Kasten, wenn der nächtliche Abgleich für die Person noch nichts geliefert hat. */
export function keineDatenText(): string {
  return "Noch keine Zahlen: Der nächtliche Abgleich hat für diese Person noch nichts geliefert.";
}

export function rundeUmsatz(wert: number): number {
  return Math.round(wert);
}

export function rundeStunden(wert: number): number {
  return Math.round(wert * 10) / 10;
}

/**
 * Ø VK auf Euro. Mit einer Zahl kommt eine Zahl zurück, nur ein fehlender Ø VK (`null`)
 * bleibt `null`. Wer einen Wert hat, der nie fehlt (das Ziel), braucht deshalb kein
 * `?? 0` — das würde einen fehlenden Wert still als 0 €/h zeigen.
 */
export function rundeVk(wert: number): number;
export function rundeVk(wert: number | null): number | null;
export function rundeVk(wert: number | null): number | null {
  return wert === null ? null : Math.round(wert);
}

/**
 * Das eine Minuszeichen der Anzeige: U+2212, das echte Minuszeichen. Jede negative Zahl, die die
 * Probezeit zeigt, trägt dieses Zeichen und nie den Bindestrich. Anlass: Im Block standen zwei
 * verschiedene Zeichen im selben Kasten, der Bindestrich in den Differenzen und U+2212 in der
 * Kostenzeile. U+2212 ist so breit wie das Plus daneben, wird als „minus“ gelesen, und die
 * Anwendung schreibt ihre negativen Zahlen schon so (`minus()` in `pages/KpiReporting.tsx`).
 *
 * Im Quelltext steht es als Escape (\u2212), damit niemand es für einen Bindestrich hält
 * und glattzieht.
 *
 * Das Vorzeichen kommt nicht aus `toLocaleString`: Es setzt den Bindestrich (`-1.234`) und sogar
 * bei −0 ein Minus (`-0`). Deshalb wird nur der Betrag formatiert, das Vorzeichen setzt `betragText`.
 */
const MINUS = "\u2212";

/**
 * Platzhalter, wo ein Wert fehlt: kein Ø VK, solange nichts gebucht ist, keine Differenz, in der Liste
 * jede Zelle ohne Zahl. Der Halbgeviertstrich ist der einzige, den die Probezeit zeigt (der Erklärtext
 * zum Ø VK nennt ihn ebenfalls). Die Bausteine importieren ihn und schreiben keinen eigenen.
 */
export const KEIN_WERT = "–";

export type Einheit = "€" | "h" | "€/h";

/** Welche Rundung zu welcher Einheit gehört (R3): Euro und Ø VK ganz, Stunden auf 0,1. */
const RUNDEN: Record<Einheit, (wert: number) => number> = {
  "€": rundeUmsatz,
  h: rundeStunden,
  "€/h": rundeVk,
};

/**
 * Ein bereits gerundeter Wert als Text: Betrag in deutscher Zahlenform, Einheit dahinter. Das
 * Vorzeichen kommt vom gerundeten Wert selbst, deshalb gibt es kein „−0“ (Math.round(-0,3) ist −0,
 * und −0 ist weder kleiner noch größer als 0). Das Plus steht nur, wo `mitPlus` es verlangt.
 */
function betragText(gerundet: number, einheit: Einheit, mitPlus: boolean): string {
  const vorzeichen = gerundet < 0 ? MINUS : mitPlus && gerundet > 0 ? "+" : "";
  return `${vorzeichen}${Math.abs(gerundet).toLocaleString("de-DE")} ${einheit}`;
}

/** Euro für einen Text: auf ganze Euro gerundet wie die Tabelle, „1.234 €“. */
export function euroText(wert: number): string {
  return betragText(rundeUmsatz(wert), "€", false);
}

/** Stunden für einen Text: auf 0,1 h gerundet wie die Tabelle, „18,5 h“. */
export function stundenText(wert: number): string {
  return betragText(rundeStunden(wert), "h", false);
}

/** Ø VK für einen Text: auf ganze €/h gerundet wie die Tabelle, „130 €/h“. Ohne Wert ein Strich, nie 0 €/h. */
export function vkText(wert: number | null): string {
  return wert === null ? KEIN_WERT : betragText(rundeVk(wert), "€/h", false);
}

/**
 * Differenz für einen Text: „+1.234 €“, „−3,4 h“, „0 €“. Der Wert wird in der Einheit der Zeile
 * gerundet (`RUNDEN`), und aus dem gerundeten Wert folgt das Vorzeichen. Wer die Differenz aus den
 * angezeigten Werten bildet (`diffAusAnzeige`, `vkDiffAusAnzeige`), reicht sie unverändert durch:
 * Rundet man eine schon gerundete Zahl noch einmal, bleibt sie, wie sie ist. Ohne Differenz ein Strich.
 */
export function diffText(wert: number | null, einheit: Einheit): string {
  return wert === null ? KEIN_WERT : betragText(RUNDEN[einheit](wert), einheit, true);
}

/**
 * Quote als Prozenttext („26 %"). Ohne Quote gibt es keinen Text.
 *
 * Wird **abgerundet**, nicht kaufmännisch gerundet (Marie 08.10.2026: „ja
 * abrunden"): Das Backend bewertet die rohe Quote (`quote >= 0.90` grün,
 * `>= 0.70` gelb). Abgerundet gilt „angezeigt ≥ 90 %" genau dann, wenn die
 * Ampel grün ist — die Zahl kann der Ampel also nicht widersprechen.
 * Kaufmännisch gerundet zeigte 0,8999 „90 %" neben „beobachten", während der
 * Erklärtext „ab 90 % grün" verspricht.
 */
export function quoteProzent(quote: number | null): string | null {
  return quote === null ? null : `${Math.floor(quote * 100)} %`;
}

/**
 * Differenz aus den ANGEZEIGTEN Werten (R3): Sonst steht in der Tabelle eine
 * Differenz, die nicht zu den beiden Zahlen darüber passt.
 */
export function diffAusAnzeige(
  ist: number,
  ziel: number,
  runden: (wert: number) => number,
): number {
  return runden(runden(ist) - runden(ziel));
}

/** Ø-VK-Differenz aus den ANGEZEIGTEN Werten (R3). Ohne Ist-Wert keine Differenz. */
export function vkDiffAusAnzeige(ist: number | null, ziel: number): number | null {
  const istGerundet = rundeVk(ist);
  return istGerundet === null ? null : istGerundet - rundeVk(ziel);
}

/**
 * Nur die Umsatz-Differenz ist farbig (R3). Ein Plus ist immer grün: Es heißt Quote
 * über 100 %, und dort ist die Ampel grün oder noch nicht gestartet. Ein Minus bekommt
 * die Ampelfarbe, aber nur bei Gelb und Rot. Bei grüner oder fehlender Ampel bleibt es
 * farblos — sonst widerspräche die Zahl der Ampel daneben.
 *
 * Gibt die TEXTfarbe zurück (`AMPEL_TEXTFARBE`), weil sie eine Zahl färbt.
 */
export function umsatzDiffFarbe(diff: number, ampel: ProbezeitAmpel): string | null {
  if (diff > 0) return AMPEL_TEXTFARBE.gruen;
  if (diff < 0 && (ampel === "gelb" || ampel === "rot")) return AMPEL_TEXTFARBE[ampel];
  return null;
}

/** Die Woche in der Spalte der Liste: „6 / 26" (Spezifikation R2). */
export function wocheText(woche: number, wochenGesamt: number): string {
  return `${woche} / ${wochenGesamt}`;
}

/**
 * Die Woche im Kopf des Blocks im Personenfenster: „13 von 26 Wochen gezählt“.
 *
 * Bewusst NICHT „Woche 13 von 26“, obwohl die Spezifikation (R7, Zeile 328) das so schreibt:
 * `woche` ist die Zahl der gezählten Wochen, und eine Woche zählt erst 7 Tage nach ihrem Ende.
 * Wer 99 Tage nach dem Eintritt dasteht, ist in Woche 15, gezählt sind 13. „Woche 13“ läse sich
 * wie die Woche der Person, und die Führungskraft plante ihr Gespräch zwei Wochen zu spät. Es ist
 * dasselbe Missverständnis, das die Linie im Wochenbalken nicht „heute“ heißen lässt. Die Spalte
 * der Liste bleibt bei „13 / 26“ (`wocheText`); am Kopf erklärt das i die Zählregel.
 */
export function wocheSatz(woche: number, wochenGesamt: number): string {
  return `${woche} von ${wochenWort(wochenGesamt)} gezählt`;
}

export function nichtGerechnetText(grund: NichtGerechnetGrund): string {
  switch (grund) {
    case "mtb":
      return "MTB: Steuerung über Auftragseingang";
    case "stufe":
      return "Stufe ohne Anlaufziel";
    case "segment":
      return "nicht im Kundensegment";
  }
}

/**
 * Satz im Kasten „Wird nicht gerechnet“: „Wird nicht gerechnet. MTB: Steuerung über Auftragseingang.“
 * Für den Grund „segment“ zeigt der Block gar keinen Kasten (R7); der Satz ist dafür nicht gedacht.
 */
export function nichtGerechnetSatz(grund: NichtGerechnetGrund): string {
  return `Wird nicht gerechnet. ${nichtGerechnetText(grund)}.`;
}

export function teilzeitText(
  wochenstunden: number | null,
  vollzeitStunden = VOLLZEIT_STUNDEN,
): string | null {
  if (wochenstunden === null || wochenstunden >= vollzeitStunden) return null;
  return `Teilzeit ${wochenstunden.toLocaleString("de-DE")} h`;
}

/**
 * Kopfzeile des Blocks: „13 von 26 Wochen gezählt · endet 28.12.“, bei Teilzeit mit dem Zusatz
 * „· Teilzeit 32 h: Ziel und Kosten anteilig“. Die Wochen stehen als gezählte Wochen da und
 * nicht als „Woche 13“ (Begründung bei `wocheSatz`).
 *
 * **Ohne das Präfix „Probezeit:“**: Mit Präfix brauchte die Zeile 288,5 px, neben der Ampel-Pille
 * bleiben 248 px (Mitarbeiterfenster) und 288 px (Onboarding-Fenster) — sie brach also um und
 * machte die Karte 18 px höher. Der Kasten ist als Probezeit-Block überschrieben und steht im
 * Fenster einer Person in der Probezeit; das Wort sagt dort nichts, was nicht schon dasteht.
 */
export function kopfzeileText(
  ergebnis: Pick<ProbezeitErgebnis, "woche" | "wochenGesamt">,
  person: Pick<ProbezeitZeile, "probezeitEnde" | "wochenstunden">,
): string {
  const teile = [
    wocheSatz(ergebnis.woche, ergebnis.wochenGesamt),
    `endet ${ddMM(person.probezeitEnde)}`,
  ];
  const teilzeit = teilzeitText(person.wochenstunden);
  if (teilzeit !== null) teile.push(`${teilzeit}: Ziel und Kosten anteilig`);
  return teile.join(" · ");
}

/**
 * Beschriftung der Pille im Kopf des Blocks: „80 % · beobachten“. Solange keine Ampel bewertet,
 * steht nur „noch keine Ampel“, ohne Quote.
 */
export function ampelPilleText(ampel: ProbezeitAmpel, quote: number | null): string {
  if (ampel === "keine") return AMPEL_URTEIL.keine;
  return `${quoteProzent(quote) ?? KEIN_WERT} · ${AMPEL_URTEIL[ampel]}`;
}

/**
 * Kostenzeile unter der Tabelle: „Vollkosten bis Woche 11: 38.640 € → DB −9.200 €“.
 *
 * Der DB wird aus den ANGEZEIGTEN Werten gebildet (R3): Ist-Umsatz (Tabelle) und Vollkosten (diese
 * Zeile), jeweils auf ganze Euro gerundet. Der Rohwert des Backends (`db`) geht nicht ein. Sonst
 * stünde hier ein DB, der um 1 € von „Ist-Umsatz minus Vollkosten“ abweicht, wie der Leser es mit
 * den Zahlen darüber nachrechnet.
 */
export function kostenzeileText(
  ergebnis: Pick<ProbezeitErgebnis, "woche" | "vollkosten" | "istUmsatz">,
): string {
  const db = diffAusAnzeige(ergebnis.istUmsatz, ergebnis.vollkosten, rundeUmsatz);
  return `Vollkosten bis Woche ${ergebnis.woche}: ${euroText(ergebnis.vollkosten)} → DB ${diffText(db, "€")}`;
}

/** Der Satz, wenn die Zahlen nicht geladen werden konnten. Wortlaut vorgegeben (08.10.2026), nicht umformulieren. */
const FEHLERSATZ = "Die Probezeit-Zahlen sind gerade nicht abrufbar.";

/**
 * Was der Block zeigt, wenn der Abruf der Person fehlschlägt.
 *
 * Zwei Fälle bleiben stumm, und nur diese zwei: „Person nicht in der Probezeit" (404) — dann
 * gibt es richtigerweise keinen Block — und „diese Rolle darf die Zahlen nicht sehen" (403),
 * denn das ist keine Störung, sondern eine Rolle ohne Zugriff auf das Onboarding-Board. Ein
 * Satz über nicht abrufbare Zahlen wäre dort falsch und würde jede Einhängestelle zwingen, die
 * Rollen selbst zu prüfen.
 *
 * Jeder andere Fehler (Serverfehler, nicht erreichbar, oder gar kein Statuscode wie bei einem
 * Netzfehler) bekommt den kurzen Satz, sonst verschwindet der Block still und niemand merkt es.
 */
export function probezeitFehlerText(status: number | undefined): string | null {
  return status === 404 || status === 403 ? null : FEHLERSATZ;
}

/**
 * Beschriftung der Krankheits-Kacheln. In der Probezeit ist „12M" derselbe
 * Zeitraum wie „seit Eintritt" — sie dauert höchstens 26 Wochen. Deshalb sagt
 * die Kachel das dort auch, sonst stünde dieselbe Zahl unter zwei
 * Beschriftungen: die Liste zeigt genau diese Werte (`probezeit-liste.ts`
 * nimmt `sickDays12m` und `sickEpisodes12m`). Die 3-Monats-Kachel bleibt außen
 * vor, „letzte 3 Monate" ist auch in der Probezeit ein echter Teilzeitraum.
 * Marie 08.10.2026: „a machen".
 */
export function krankheitKachel(seitEintritt: boolean): { tage: string; meldungen: string } {
  return seitEintritt
    ? { tage: "Arbeitstage seit Eintritt", meldungen: "Krankmeldungen seit Eintritt" }
    : { tage: "Arbeitstage / 12M", meldungen: "Krankmeldungen / 12M" };
}

/** Zusatz am i, solange die Kacheln „seit Eintritt" sagen. */
export const KRANKHEIT_ZUSATZ_SEIT_EINTRITT =
  "In der Probezeit ist das derselbe Zeitraum wie seit dem Eintritt, weil sie höchstens 26 Wochen dauert.";

/** „8 h in Woche 1–2, 12 h in Woche 3–4, …" — für den Erklärtext am i. */
export function anlaufInWorten(zielstunden: readonly number[]): string {
  const stuecke: string[] = [];
  let start = 0;
  for (let i = 1; i <= zielstunden.length; i++) {
    if (i === zielstunden.length || zielstunden[i] !== zielstunden[start]) {
      const bereich = i - start === 1 ? `Woche ${start + 1}` : `Woche ${start + 1}–${i}`;
      stuecke.push(`${zielstunden[start]} h in ${bereich}`);
      start = i;
    }
  }
  return stuecke.join(", ");
}

/** Stufenabhängiger Zusatz am i der Spalte „Stunden" (R7). */
export function zusatzStunden(stufe: string, werte: ProbezeitStufe): string {
  return `Anlaufziel ${stufe}: ${anlaufInWorten(werte.zielstunden)}`;
}

/**
 * Stufenabhängiger Zusatz am i der Spalte „Ø VK" (R7, mit Listenpreis). Ziel und Listenpreis
 * stehen auf ganze €/h gerundet, wie die Tabelle (R3): Was der Erklärtext sagt, muss zu dem
 * passen, was die Tabelle zeigt.
 */
export function zusatzVk(stufe: string, werte: ProbezeitStufe): string {
  return `${stufe}: Ziel ${rundeVk(werte.vkZiel).toLocaleString("de-DE")} €/h · Listenpreis ${rundeVk(werte.listenpreis).toLocaleString("de-DE")} €/h`;
}

/** Stufenabhängiger Zusatz am i der Kostenzeile (R7). Wochenkosten auf ganze Euro, wie die Kostenzeile (R3). */
export function zusatzVollkosten(stufe: string, werte: ProbezeitStufe): string {
  return `${stufe}: ${rundeUmsatz(werte.vollkostenJeWoche).toLocaleString("de-DE")} € Vollkosten je Woche, bei Vollzeit`;
}

// ── Liste „In der Probezeit“ (R1, R2) ───────────────────────────────────────────
// Alle Texte der Zeilen entstehen hier. Die Liste (`components/probezeit/ProbezeitListe.tsx`) ordnet
// sie nur an: Sie rundet nichts, schreibt keine Zahl in deutscher Form und setzt keinen Satz zusammen.

/**
 * „2 Krankmeldungen“, „1 Tag“: Zahl in deutscher Form, die Einzahl nur bei genau 1. Für Arbeitstage
 * gibt es `arbeitstageText`, das die Liste benutzt, statt es nachzubauen.
 */
function zaehlwort(n: number, einzahl: string, mehrzahl: string): string {
  return `${n.toLocaleString("de-DE")} ${n === 1 ? einzahl : mehrzahl}`;
}

/**
 * Beschriftung der Pille in der Spalte „Umsatz gegen Ziel“ (R2): „95 % auf Kurs“. Vor der ersten
 * Bewertung (Woche 1 bis 3) „noch keine Ampel · bisher 26 %“, ohne Quote nur „noch keine Ampel“.
 * Im Block des Personenfensters steht dagegen „95 % · auf Kurs“ (`ampelPilleText`, R7): Beide Formen
 * stehen so in der Spezifikation.
 */
export function ampelListeText(ampel: ProbezeitAmpel, quote: number | null): string {
  const prozent = quoteProzent(quote);
  if (ampel === "keine") {
    return prozent === null ? AMPEL_URTEIL.keine : `${AMPEL_URTEIL.keine} · bisher ${prozent}`;
  }
  return `${prozent ?? KEIN_WERT} ${AMPEL_URTEIL[ampel]}`;
}

/** Zeile unter dem Ist-Wert: „Ziel 20 h“. Der Wert kommt gerundet aus `euroText`, `stundenText` oder `vkText`. */
export function zielText(wert: string): string {
  return `Ziel ${wert}`;
}

/** Zeile unter der Pille: „Umsatz 12.346 € · Ziel 13.000 €“, beide auf ganze Euro gerundet wie die Tabelle (R3). */
export function umsatzZielText(e: Pick<ProbezeitErgebnis, "istUmsatz" | "zielUmsatz">): string {
  return `Umsatz ${euroText(e.istUmsatz)} · ${zielText(euroText(e.zielUmsatz))}`;
}

/** Die zwei Zeilen der Spalte „Eigene Krankheitstage“. Die zweite fehlt, wo es keine Krankmeldung gibt. */
export interface KrankheitZelle {
  /** „4 Arbeitstage“ */
  tage: string;
  /** „2 Krankmeldungen“; null bei keiner Krankmeldung (R2: „bei 0 nur „0 Arbeitstage““) */
  meldungen: string | null;
}

/**
 * Eigene Krankheitstage der Zeile (R2). Ohne Krankheitsdaten (künftiger Eintritt, kein Abgleich) steht
 * der Strich, nie „0 Arbeitstage“: Null Arbeitstage sind ein Wert, ein fehlender ist keiner.
 */
export function krankheitZelle(krankheit: ProbezeitZeile["krankheit"]): KrankheitZelle {
  if (krankheit === null) return { tage: KEIN_WERT, meldungen: null };
  return {
    tage: arbeitstageText(krankheit.tage),
    meldungen:
      krankheit.meldungen > 0
        ? zaehlwort(krankheit.meldungen, "Krankmeldung", "Krankmeldungen")
        : null,
  };
}

/** Ein Tag in Millisekunden. Die Tage werden in UTC gezählt: Dort gibt es keine Zeitumstellung. */
const TAG_MS = 86_400_000;

/**
 * Bis zu so vielen Tagen vor dem Ende der Probezeit ist das Datum hervorgehoben (R2). Der freigegebene
 * Erklärtext zu „Probezeit endet“ verspricht: „Die letzten 30 Tage sind hervorgehoben.“
 */
const ENDE_HERVORGEHOBEN_TAGE = 30;

/**
 * Der Tag, den die Oberfläche als „heute“ nimmt: derselbe UTC-Tag wie im Backend
 * (`routes/probezeit.ts`: `new Date().toISOString().slice(0, 10)`). Sonst stünde „noch N Tage“ neben
 * einer Liste, die der Server für einen anderen Tag gebaut hat.
 */
export function heuteIso(jetzt: Date = new Date()): string {
  return jetzt.toISOString().slice(0, 10);
}

/** Kalendertage von `heute` bis `ende`, beide als „JJJJ-MM-TT“. Negativ, wenn das Ende schon vorbei ist. */
export function tageBisEnde(ende: string, heute: string): number {
  return (Date.parse(ende.slice(0, 10)) - Date.parse(heute.slice(0, 10))) / TAG_MS;
}

/** Die Spalte „Probezeit endet“: Datum, Zeile darunter und ob das Datum hervorgehoben wird. */
export interface EndeAnzeige {
  /** „28.12.“ */
  datum: string;
  /** „noch 80 Tage“, „endet heute“, „Probezeit beendet“, oder „Austritt zum 20.10.“, wenn die Person ohnehin geht (R1) */
  zusatz: string;
  /** Die letzten 30 Tage der Probezeit, das Ende selbst und alles danach */
  bald: boolean;
}

/**
 * Die Zeile unter dem Ende der Probezeit, wenn die Person bleibt. Drei Fälle, drei Texte (Plan A2,
 * Task 6, Nachtrag Punkt 1 und Nachtrag 2 Punkt 7):
 * - noch Tage übrig: „noch 80 Tage“, „noch 1 Tag“,
 * - der letzte Tag: „endet heute“. „noch 0 Tage“ läse sich wie abgelaufen, und abgelaufen ist die
 *   Probezeit an diesem Tag gerade nicht,
 * - danach: „Probezeit beendet“. „endet heute“ wäre am Tag darauf um einen Tag falsch.
 */
function endeZusatz(tage: number): string {
  if (tage > 0) return `noch ${zaehlwort(tage, "Tag", "Tage")}`;
  return tage === 0 ? "endet heute" : "Probezeit beendet";
}

/**
 * Spalte „Probezeit endet“ (R2): Datum, darunter „noch N Tage“. Wer schon ein Austrittsdatum hat,
 * bekommt dort „Austritt zum TT.MM.“ (R1), auch am letzten Tag und danach. Dass das Ende schon
 * vorbei ist, tritt normalerweise nicht auf, weil die Person aus der Liste fällt (so sagt es der
 * Erklärtext); Client und Server können sich aber um einen Tag unterscheiden, und eine Anzeige darf
 * dann nicht lügen: nie eine negative Zahl, nie „endet heute“ nach dem Ende.
 */
export function endeAnzeige(
  zeile: Pick<ProbezeitZeile, "probezeitEnde" | "austrittZum">,
  heute: string,
): EndeAnzeige {
  const tage = tageBisEnde(zeile.probezeitEnde, heute);
  return {
    datum: ddMM(zeile.probezeitEnde),
    zusatz:
      zeile.austrittZum !== null ? `Austritt zum ${ddMM(zeile.austrittZum)}` : endeZusatz(tage),
    bald: tage <= ENDE_HERVORGEHOBEN_TAGE,
  };
}

const BINDUNG_TEXT = { low: "gering", medium: "mittel", high: "hoch" } as const;

/** Text der Bindungs-Stufe in den -text-Marken der Ampel (>= 4,5 : 1), nie in den Füllfarben. */
const BINDUNG_FARBE = {
  low: AMPEL_TEXTFARBE.gruen,
  medium: AMPEL_TEXTFARBE.gelb,
  high: AMPEL_TEXTFARBE.rot,
} as const;

/** Die Spalte „Bindung“: der Text und seine Farbe (null = keine eigene Farbe). */
export interface BindungAnzeige {
  text: string;
  farbe: string | null;
}

/**
 * Spalte „Bindung“ (R2): die Stufe des Bindungsrisikos als Wort in der Farbe der Stufe. Bei bestätigter
 * Kündigung steht stattdessen „Kündigung bestätigt“ (der Erklärtext zur Spalte sagt es so), ohne
 * Farbe: Die Person gilt nicht mehr als Risiko. Ohne Stufe (künftiger Eintritt) der Strich.
 */
export function bindungAnzeige(
  zeile: Pick<ProbezeitZeile, "bindung" | "kuendigungBestaetigt">,
): BindungAnzeige {
  if (zeile.kuendigungBestaetigt) return { text: "Kündigung bestätigt", farbe: null };
  if (zeile.bindung === null) return { text: KEIN_WERT, farbe: null };
  return { text: BINDUNG_TEXT[zeile.bindung], farbe: BINDUNG_FARBE[zeile.bindung] };
}

/**
 * Zeile unter dem Namen (R2): „Senior Consultant · Team Beispiel · Teilzeit 32 h“, fehlende Teile
 * entfallen. Ein künftiger Eintritt sagt zuerst, wann er startet (R1: „startet am TT.MM.“). Die
 * Wirtschaftsspalten fehlen bei Führungskräften außerhalb des Kundensegments, der Name ist die einzige
 * Stelle, die in jeder Liste da ist.
 */
export function untertitelText(
  zeile: Pick<ProbezeitZeile, "position" | "team" | "wochenstunden" | "lage" | "eintritt">,
): string {
  return [
    zeile.lage === "kuenftig" ? startetAmText(zeile.eintritt) : null,
    zeile.position,
    zeile.team,
    teilzeitText(zeile.wochenstunden),
  ]
    .filter((teil): teil is string => !!teil)
    .join(" · ");
}

/**
 * Wer im Filter „Führungskraft“ der Liste steht: jede einmal, nach Namen in deutscher Reihenfolge.
 * Führungskräfte ohne Namen lassen sich nicht auswählen und fehlen.
 */
export function fuehrungskraefteVon(
  zeilen: ReadonlyArray<Pick<ProbezeitZeile, "fuehrungskraft">>,
): Array<{ id: number; name: string }> {
  const nachId = new Map<number, string>();
  for (const { fuehrungskraft } of zeilen) {
    if (fuehrungskraft?.name) nachId.set(fuehrungskraft.id, fuehrungskraft.name);
  }
  return [...nachId]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** Die Spalte „Führungskraft“ einer Zeile: der Name, ohne Führungskraft oder ohne Namen der Strich. */
export function fuehrungskraftText(fuehrungskraft: ProbezeitZeile["fuehrungskraft"]): string {
  return fuehrungskraft?.name ?? KEIN_WERT;
}

/** Was die Liste von der Führungskraft zeigt: Auswahl, Spalte, Filter und die geltende Wahl. */
export interface FuehrungskraftAnzeige {
  /** Wer im Filter zur Wahl steht: jede Führungskraft der ungefilterten Liste, nach Namen */
  auswahl: Array<{ id: number; name: string }>;
  /** Die Spalte „Führungskraft“ in der Tabelle */
  spalte: boolean;
  /** Die Auswahl über der Tabelle */
  filter: boolean;
  /** Die Führungskraft, auf die die Liste gerade eingeschränkt ist; null = alle */
  gewaehlt: number | null;
}

/**
 * Spalte und Filter „Führungskraft“ (Plan A2, Task 6, Nachtrag 2 Punkt 6): Sie folgen den Daten, nicht
 * der Rolle. Sie erscheinen, sobald die **ungefilterte** Liste mehr als eine Führungskraft enthält. Das
 * stimmt für HR und Vorstand und ebenso für eine Führungskraft mit Unterlinien, denn `computeScope`
 * gibt ihr die transitive Linie plus Consulting-Zuordnungen, und die Führungskraft einer Zeile ist
 * `consultingManagerId ?? managerId`: Sie sieht verschiedene Namen je Zeile. Die Spalte fehlt nur dort,
 * wo sie in jeder Zeile dasselbe sagen würde.
 *
 * Gezählt werden Führungskräfte mit Namen, nicht Zeilen (`fuehrungskraefteVon`): Wen die Liste nicht
 * nennen kann, den kann sie auch nicht zur Wahl stellen.
 *
 * Der Filter bleibt zusätzlich sichtbar, solange einer gesetzt ist. Sonst säße HR bei einer Auswahl, die
 * nur eine Führungskraft übrig lässt, in der Falle: Der Filter wäre weg, die Liste bliebe stehen.
 *
 * Eine Wahl, die es in den Daten nicht mehr gibt, gilt nicht mehr (`gewaehlt` ist dann null). Die Daten
 * werden neu geladen, und die gewählte Führungskraft hat vielleicht keine Person mehr in der Probezeit.
 * Ohne diese Regel zeigte die Auswahl „Alle“, die Liste bliebe leer, und ein Klick auf „Alle“ löste
 * nichts aus, weil die Auswahl es schon zeigt.
 */
export function fuehrungskraftAnzeige(
  ungefilterteZeilen: ReadonlyArray<Pick<ProbezeitZeile, "fuehrungskraft">>,
  gewaehlt: number | null,
): FuehrungskraftAnzeige {
  const auswahl = fuehrungskraefteVon(ungefilterteZeilen);
  const gilt = gewaehlt !== null && auswahl.some((f) => f.id === gewaehlt);
  const spalte = auswahl.length > 1;
  return { auswahl, spalte, filter: spalte || gilt, gewaehlt: gilt ? gewaehlt : null };
}

/**
 * Zeile unter der Liste: „Letzter Abgleich: 08.10.“ Der Tag des letzten nächtlichen Abgleichs (`stand`
 * der Schnittstelle, ein Zeitpunkt in UTC).
 *
 * Bewusst nicht „Stand“ (Plan A2, Task 6, Nachtrag Punkt 3): Das Wort steht unter dem Wochenbalken
 * und meint dort die letzte gezählte Leistung (`standZeile`, Maries Wortlaut vom 08.10.2026).
 * „Stand“ darf nicht zweimal etwas anderes heißen. Wer „Stand 09.10.“ unter der Liste läse, glaubte
 * an Buchungen bis zum 09.10., gezählt wird aber erst sieben Tage nach dem Ende einer Woche.
 */
export function standListeText(stand: string): string {
  return `Letzter Abgleich: ${ddMM(stand)}`;
}

/**
 * Das Ergebnis, wenn die Zeile Zahlen zeigen darf: Die Person ist gerechnet und mindestens eine Woche ist
 * gezählt. In Woche 0 sind Ziel und Ist rechnerisch 0, aber „Umsatz 0 € · Ziel 0 €“ liest sich als „hat
 * nichts gebucht“, während das Personenfenster derselben Person den Woche-0-Satz zeigt (`satzText`,
 * „Noch keine Woche gezählt …“). Also null, und die Zellen schreiben den Strich (Plan A2, Task 6,
 * Nachtrag 2 Punkt 9). Die Spalte „Woche“ braucht diese Regel nicht: Sie zeigt in Woche 0 weiter „0 / 26“.
 */
function ergebnisMitZahlen(wirtschaft: ProbezeitWirtschaft): ProbezeitErgebnis | null {
  return wirtschaft.status === "gerechnet" && wirtschaft.ergebnis.woche > 0
    ? wirtschaft.ergebnis
    : null;
}

/** Spalte „Woche“ (R2): „6 / 26“, in Woche 0 „0 / 26“; ohne Rechnung der Strich. */
export function wocheZelle(wirtschaft: ProbezeitWirtschaft): string {
  return wirtschaft.status === "gerechnet"
    ? wocheText(wirtschaft.ergebnis.woche, wirtschaft.ergebnis.wochenGesamt)
    : KEIN_WERT;
}

/**
 * Die Zelle der Spalte „Umsatz gegen Ziel“: entweder die Pille mit der Zeile darunter, oder ein Text
 * mit einer optionalen Zeile darunter (Grund, warum nichts gerechnet wird).
 */
export type UmsatzZelle =
  | { art: "pille"; ampel: ProbezeitAmpel; pille: string; zeile: string }
  | { art: "text"; text: string; zeile: string | null };

/**
 * Spalte „Umsatz gegen Ziel“ (R2). Gerechnet: die Pille „95 % auf Kurs“ und darunter „Umsatz X € · Ziel
 * Y €“. Nicht gerechnet: „wird nicht gerechnet“ mit dem Grund (R2 schreibt den Wortlaut vor). Ohne
 * Abgleich: „noch keine Zahlen“, kurz für die Zelle; der Kasten im Personenfenster schreibt den ganzen
 * Satz (`keineDatenText`). In Woche 0 und bei einem künftigen Eintritt: der Strich.
 */
export function umsatzZelle(wirtschaft: ProbezeitWirtschaft): UmsatzZelle {
  switch (wirtschaft.status) {
    case "gerechnet": {
      const e = ergebnisMitZahlen(wirtschaft);
      if (e === null) return { art: "text", text: KEIN_WERT, zeile: null };
      return {
        art: "pille",
        ampel: e.ampel,
        pille: ampelListeText(e.ampel, e.quote),
        zeile: umsatzZielText(e),
      };
    }
    case "nichtGerechnet":
      return {
        art: "text",
        text: "wird nicht gerechnet",
        zeile: nichtGerechnetText(wirtschaft.grund),
      };
    case "keineDaten":
      return { art: "text", text: "noch keine Zahlen", zeile: null };
    case "kuenftig":
      return { art: "text", text: KEIN_WERT, zeile: null };
  }
}

/** Eine Zelle mit dem Wert und, darunter, dem Ziel (Stunden, Ø VK). Ohne Wert: der Strich, ohne Ziel. */
export interface WertZelle {
  wert: string;
  /** „Ziel 20 h“; null, wo es keinen Wert gibt */
  ziel: string | null;
}

const OHNE_WERT: WertZelle = { wert: KEIN_WERT, ziel: null };

/** Spalte „Stunden“ (R2): die Ist-Stunden, darunter „Ziel X h“. */
export function stundenZelle(wirtschaft: ProbezeitWirtschaft): WertZelle {
  const e = ergebnisMitZahlen(wirtschaft);
  if (e === null) return OHNE_WERT;
  return { wert: stundenText(e.istStunden), ziel: zielText(stundenText(e.zielStunden)) };
}

/**
 * Spalte „Ø VK“ (R2): der Ist-Wert in €/h, darunter „Ziel X €/h“. Ohne Ø VK (nur Festpreis-Stunden)
 * steht der Strich und das Ziel bleibt, nie 0 €/h.
 */
export function vkZelle(wirtschaft: ProbezeitWirtschaft): WertZelle {
  const e = ergebnisMitZahlen(wirtschaft);
  if (e === null) return OHNE_WERT;
  return { wert: vkText(e.vkIst), ziel: zielText(vkText(e.vkZiel)) };
}

/** Wann die Liste keine Zeilen zeigt, sondern einen Satz. */
export type ListenZustand = "laedt" | "fehler" | "leer";

/** Der Satz, wenn die Liste lädt, nicht geladen werden konnte oder leer ist. */
export function listenHinweisText(zustand: ListenZustand): string {
  switch (zustand) {
    case "laedt":
      return "Liste „In der Probezeit“ wird geladen …";
    case "fehler":
      return "Die Liste „In der Probezeit“ konnte nicht geladen werden.";
    case "leer":
      return "Gerade ist niemand in der Probezeit.";
  }
}

/** Die Spalten der Liste; der Schlüssel ist zugleich der Schlüssel der Sortierregel (`probezeitSortSpalten`). */
export type ListenSpalte =
  | "name"
  | "fuehrungskraft"
  | "woche"
  | "umsatz"
  | "stunden"
  | "vk"
  | "krankheit"
  | "ende"
  | "bindung";

/** Der Kopf einer Spalte: was dasteht und ob ein Info-Symbol daneben sitzt. */
export interface SpaltenKopf {
  spalte: ListenSpalte;
  label: string;
  /** Schlüssel des Erklärtexts in `lib/glossar.ts`; Name und Führungskraft haben keinen */
  info?: string;
  /** Blasser Zusatz hinter dem Namen der Spalte, der nicht zum Sortier-Label gehört */
  zusatz?: string;
  /** Zahlenspalten stehen rechtsbündig */
  rechts?: boolean;
}

/** Zu welcher Gruppe eine Spalte gehört, und damit, wann sie da ist. */
type SpaltenGruppe = "immer" | "fuehrungskraft" | "wirtschaft";

/**
 * Die Spalten der Liste in der Reihenfolge von R2. Kopf, Raster und Zeilen lesen diese eine Liste und
 * führen keine eigenen Bedingungen: Sonst müssten drei Stellen von Hand zusammenpassen.
 *
 * „Bindung“ heißt weiter so (Plan A2, Task 6, Nachtrag Punkt 2): Das Tool nennt diese Stufe überall
 * so (`MeinTeam.tsx`, `EmployeeDetailDrawer.tsx`), Marie arbeitet damit.
 */
const SPALTEN: ReadonlyArray<SpaltenKopf & { gruppe: SpaltenGruppe }> = [
  { spalte: "name", label: "Mitarbeiter", zusatz: "↗ Detail", gruppe: "immer" },
  { spalte: "fuehrungskraft", label: "Führungskraft", gruppe: "fuehrungskraft" },
  { spalte: "woche", label: "Woche", info: "probezeitWoche", rechts: true, gruppe: "wirtschaft" },
  {
    spalte: "umsatz",
    label: "Umsatz gegen Ziel",
    info: "probezeitUmsatzGegenZiel",
    rechts: true,
    gruppe: "wirtschaft",
  },
  {
    spalte: "stunden",
    label: "Stunden",
    info: "probezeitStunden",
    rechts: true,
    gruppe: "wirtschaft",
  },
  { spalte: "vk", label: "Ø VK", info: "probezeitVk", rechts: true, gruppe: "wirtschaft" },
  {
    spalte: "krankheit",
    label: "Eigene Krankheitstage",
    info: "probezeitKrankheitstage",
    rechts: true,
    gruppe: "immer",
  },
  { spalte: "ende", label: "Probezeit endet", info: "probezeitEnde", gruppe: "immer" },
  { spalte: "bindung", label: "Bindung", info: "probezeitBindung", gruppe: "immer" },
];

/**
 * Die Spalten, die die Liste gerade zeigt. `fuehrungskraft` sagt `fuehrungskraftAnzeige`, `wirtschaft`
 * das Kennzeichen `zeigeWirtschaftsspalten` des Backends (R2: Es hängt an den Personen der Liste, nicht an
 * der Rolle).
 */
export function listenSpalten(zeige: {
  fuehrungskraft: boolean;
  wirtschaft: boolean;
}): SpaltenKopf[] {
  return SPALTEN.filter(({ gruppe }) => gruppe === "immer" || zeige[gruppe]).map(
    ({ gruppe: _gruppe, ...kopf }) => kopf,
  );
}

/**
 * Unter dieser Zahl Personen bestimmen Einzelfälle die Prozentwerte. Die Spezifikation nennt keine
 * Schwelle; 10 ist gesetzt (Plan A2, Task 6, Nachtrag 2 Punkt 8).
 */
const WENIGE_PERSONEN = 10;

/**
 * Hinweis an einer Quoten-Kachel, dass ihr Prozentwert auf wenigen Personen beruht (Spezifikation R7,
 * Punkt 2): „beruht auf 5 Personen“, bei einer Person „beruht auf 1 Person“. Eine Führungskraft mit vier
 * Neueinstellungen liest „75 % bestanden“ sonst als Trend.
 *
 * Der Hinweis gehört an die Kachel, zu der er passt, und kennt nur deren eigenen Nenner, die Zahl hinter
 * „von“ (Plan A2, Task 6, Nachtrag 3 Punkt 13, Marie 12.10.2026). Eine gemeinsame Zeile unter allen
 * Kacheln hätte bei den Nennern 5, 40 und 60 auch die Quoten als dünn ausgewiesen, die auf 40 und 60
 * Personen stehen. Welche Kacheln ihn bekommen, entscheidet die Seite: die drei Quoten, nicht die Anzahl
 * „Neue Mitarbeiter“ und nicht der Mittelwert der Betriebszugehörigkeit.
 *
 * Die Kachel nennt ihren Nenner schon („4 von 5“). Der Hinweis soll ihn nicht wiederholen, sondern als
 * wackelig kenntlich machen; deshalb steht nur dieser eine Satzteil da, ohne zweiten.
 *
 * Ab 10 Personen steht nichts da. Bei 0 auch nicht: Die Kachel zeigt „0 von 0“, es gibt keine Person,
 * auf der etwas beruhen könnte.
 */
export function wenigePersonenHinweis(nenner: number): string | null {
  const wenige = nenner >= 1 && nenner < WENIGE_PERSONEN;
  return wenige ? `beruht auf ${zaehlwort(nenner, "Person", "Personen")}` : null;
}

/** Rollen, die die „Auswertung für HR“ sehen (Spezifikation R7): admin und hrbp, keine andere. */
const HR_ROLLEN: readonly Role[] = ["admin", "hrbp"];

/**
 * Ob die Seite Onboarding-Erfolg die „Auswertung für HR“ zeigt: Kohorten-Vergleich, Heatmaps und
 * Mitarbeiterliste des ersten Jahres. Die Rolle kommt aus /me; mit „Ansicht als“ meldet es die Rolle der
 * Führungskraft, und die Seite zeigt deren Sicht (wie `canSeeNames` in `KpiReporting.tsx`).
 */
export function siehtHrAuswertung(rolle: Role | undefined): boolean {
  return rolle !== undefined && HR_ROLLEN.includes(rolle);
}

function ergebnisVon(zeile: ProbezeitZeile) {
  return zeile.wirtschaft.status === "gerechnet" ? zeile.wirtschaft.ergebnis : null;
}

/**
 * Standardreihenfolge der Liste (R1): Probezeit-Ende aufsteigend, künftige
 * Eintritte ans Ende — wer zuerst entschieden werden muss, steht oben. Bei Gleichstand
 * entscheidet der Name (deutsche Sortierung). Die Reihenfolge folgt dem Backend
 * (`sortiereProbezeitZeilen` in `src/services/probezeit-liste.ts`): Die Oberfläche
 * sortiert also genauso, wie der Server die Liste liefert.
 */
export function probezeitStandardReihenfolge(a: ProbezeitZeile, b: ProbezeitZeile): number {
  const kuenftig = Number(a.lage === "kuenftig") - Number(b.lage === "kuenftig");
  if (kuenftig !== 0) return kuenftig;
  return a.probezeitEnde.localeCompare(b.probezeitEnde) || a.name.localeCompare(b.name, "de");
}

/** Sortierbare Spalten der Liste; Schlüssel = `spalte` am Spaltenkopf. */
export function probezeitSortSpalten(): SortSpalten<ProbezeitZeile> {
  return {
    name: { wert: (z) => z.name, typ: "text" },
    fuehrungskraft: { wert: (z) => z.fuehrungskraft?.name ?? null, typ: "text" },
    // Die Woche ist auch in Woche 0 ein Wert: „0 / 26“ steht in der Zelle.
    woche: { wert: (z) => ergebnisVon(z)?.woche ?? null, typ: "zahl" },
    umsatz: { wert: (z) => ergebnisVon(z)?.quote ?? null, typ: "zahl" },
    // Wo die Zelle den Strich zeigt (Woche 0), ist der Wert fehlend und steht am Ende, nicht bei 0 Stunden.
    stunden: { wert: (z) => ergebnisMitZahlen(z.wirtschaft)?.istStunden ?? null, typ: "zahl" },
    vk: { wert: (z) => ergebnisVon(z)?.vkIst ?? null, typ: "zahl" },
    krankheit: { wert: (z) => z.krankheit?.tage ?? null, typ: "zahl" },
    ende: { wert: (z) => z.probezeitEnde, typ: "text" },
    bindung: {
      wert: (z) => (z.bindung === null ? null : { low: 1, medium: 2, high: 3 }[z.bindung]),
      typ: "zahl",
    },
  };
}
